import { useEffect, useRef, useState } from 'react'
import CanvasJSReact from '@canvasjs/react-charts'

import { CHART_DEFAULT_OPTIONS, SUBTITLE_FONT_SIZE, TITLE_FONT_SIZE, UserUnitSettings } from '../../common/constants'
import { convertTimestamp } from '../../common/helpers'
import { formatDateForChart, roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { DeviceInfo, LINE_COLORS, UnitCategoryId } from '../../common/enums'
import { Viewport } from '../../common/types'

const { CanvasJSChart } = CanvasJSReact

interface Props {
  device: DeviceInfo
  sensorPort: string
  sensorReadings: any
  viewport: Viewport
  userUnitSettings: UserUnitSettings
  isLoading: boolean
  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
}

export function VegetronixVH400Chart({
  device,
  sensorPort,
  sensorReadings,
  viewport,
  userUnitSettings,
  isLoading,
  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = (timestamp: number) => {},
}: Props) {
  const ref = useRef(null)

  const precision = userUnitSettings[UnitCategoryId.VolumetricWaterContent].precision

  const [x, setX] = useState(0)
  const [y, setY] = useState(0)

  let trackDay = -1

  useEffect(() => {
    if (ref === null) return

    const canvas = document.getElementsByClassName('canvasjs-chart-canvas')[1]
    canvas.addEventListener('click', onClickTest)

    function onClickTest() {
      addTimestamp(x)
    }

    return () => {
      canvas.removeEventListener('click', onClickTest)
    }
  }, [ref, x, y]) // eslint-disable-line react-hooks/exhaustive-deps

  const dataPoints = sensorReadings
    ? Object.keys(sensorReadings.readings).map((key: string) => {
        const ts = parseInt(key)
        return {
          label: convertTimestamp(ts),
          x: ts * 1000,
          y: roundToDecimal(Number(sensorReadings.readings[ts]), precision),
        }
      })
    : []

  const depth = sensorReadings.metadata.sensorConfig.offset

  const line = {
    name: `${sensorPort} (${depth}")`,
    type: 'line',
    showInLegend: true,
    xValueType: 'dateTime',
    color: LINE_COLORS[sensorPort],
    dataPoints,
  }

  // const timestampMin = Math.min(...dataPoints.map((point) => point.x))
  // const timestampMax = Math.max(...dataPoints.map((point) => point.x))

  // const fieldCapacity = 26
  // const permanentWiltingPoint = 11

  // const fieldCapacityOverlay = {
  //   name: 'Field Capacity',
  //   type: 'rangeArea',
  //   xValueType: 'dateTime',
  //   markerType: 'none',
  //   color: 'yellow',
  //   lineThickness: 0,
  //   fillOpacity: 0.1,
  //   toolTipContent: `Field Capacity: ${fieldCapacity}%`,
  //   dataPoints: [
  //     { x: timestampMin, y: [fieldCapacity, 100] },
  //     { x: timestampMax, y: [fieldCapacity, 100] },
  //   ],
  // }

  // const permanentWiltingPointOverlay = {
  //   name: 'Permanent Wilting Point',
  //   type: 'rangeArea',
  //   xValueType: 'dateTime',
  //   markerType: 'none',
  //   color: 'red',
  //   lineThickness: 0,
  //   fillOpacity: 0.1,
  //   toolTipContent: `Permanent Wilting Point: ${permanentWiltingPoint}%`,
  //   dataPoints: [
  //     { x: timestampMin, y: [0, permanentWiltingPoint] },
  //     { x: timestampMax, y: [0, permanentWiltingPoint] },
  //   ],
  // }

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_SoilMoisture`,
    title: {
      text: 'Soil Moisture',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`

        for (var i = 0; i < e.entries.length; i++) {
          content += `<span style="color:${e.entries[i].dataSeries.color}">${
            e.entries[i].dataSeries.name
          }:</span> <strong> ${roundToDecimal(e.entries[i].dataPoint.y, precision)} % </strong><br/>`
        }

        setX(e.entries[0].dataPoint.x)
        setY(e.entries[0].dataPoint.y)

        return content
      },
    },
    axisX: {
      labelFormatter: function (e: any) {
        const date = new Date(e.value)
        const day = date.getDay()
        const dayChanged = trackDay !== day
        trackDay = day

        return formatDateForChart(date, dayChanged)
      },
      viewportMinimum: viewport?.minimum || null,
      viewportMaximum: viewport?.maximum || null,
      stripLines: [
        ...selectXRange.map((value) => {
          return {
            value,
            color: 'red',
            labelPlacement: 'outside',
          }
        }),

        selectXRange.length === 2 && {
          startValue: selectXRange[0],
          endValue: selectXRange[1],
          color: '#d8d8d8',
        },
      ],
    },
    axisY: {
      includeZero: false,
      maximum: 105,
      minimum: 0,
      title: `Moisture (%)`,
      titleFontSize: TITLE_FONT_SIZE,
      labelFontSize: TITLE_FONT_SIZE,
    },
    // data: [line, fieldCapacityOverlay, permanentWiltingPointOverlay],
    data: [line],
  }

  function getLastReadingTimestamp(): string {
    const timestamps = dataPoints.map((point) => point.x)
    const lastTimestamp = Math.max(...timestamps)
    return new Date(lastTimestamp).toLocaleString()
  }

  const data = [
    [
      `Port ${sensorPort}: ${sensorReadings.metadata.productName}`,
      `${getLastReadingTimestamp()}`,
      `Depth: ${depth}"`,
      `${roundToDecimal(dataPoints[dataPoints.length - 1]?.y, precision) || 0} %`,
    ],
  ]

  return (
    <CollapsibleSection title="Soil Moisture" isLoading={isLoading}>
      <ChartSubHeader data={data} />
      <CanvasJSChart ref={ref} options={options} />
    </CollapsibleSection>
  )
}
